import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { undo } from '../../../reducers/undoable';
import { telemetry } from '../../../telemetry';
import {
  addPrompt,
  addPrompt_deprecated,
  updatePrompt as updatePromptAction,
  updatePrompt_deprecated as updatePromptAction_deprecated,
  deletePrompt,
  deletePrompt_deprecated,
  setSelectedPrompt,
  addEmptyPrompt_deprecated as addEmptyPromptAction,
  removeEmptyPrompt_deprecated as removeEmptyPromptAction,
  setPrompts,
  setLoadingPrompts,
} from '../../../reducers/promptLibraryReducer';
import { Prompt } from '@server/prompts';
import { usePromptLibraryFacade } from '../../../apis/facades/usePromptLibraryFacade';

export const usePrompt = () => {
  const { get_prompts, create_prompt, update_prompt, delete_prompt, optimize_prompt } =
    usePromptLibraryFacade();
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();

  const handleError = useCallback(
    (error: any, errorMessage: string, optimisticAction?: any) => {
      addNotification({
        message: errorMessage,
        variant: SweepNotificationVariant.Error,
      });
      dispatch(undo(optimisticAction));
      telemetry.captureError(errorMessage, error);
    },
    [addNotification, dispatch],
  );

  const loadPrompts = useCallback(async () => {
    try {
      dispatch(setLoadingPrompts({ isLoading: true }));
      const prompts = await get_prompts();
      dispatch(setPrompts({ prompts }));
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      dispatch(setLoadingPrompts({ isLoading: false }));
    }
  }, [dispatch, get_prompts]);

  // New: creates prompt with optional groupId
  const createPrompt = useCallback(
    async (promptData: { name: string; promptText: string; promptGroupId?: string }) => {
      try {
        const newPrompt = await create_prompt(promptData);
        dispatch(addPrompt({ prompt: newPrompt }));
        return newPrompt;
      } catch (error) {
        handleError(error, 'Failed to create prompt');
        return null;
      }
    },
    [dispatch, handleError, create_prompt],
  );

  // Deprecated: creates prompt that must belong to a group
  const createPrompt_deprecated = useCallback(
    async (groupId: string, promptData: Omit<Prompt, 'id'>) => {
      try {
        const newPrompt = await create_prompt({
          ...promptData,
          promptGroupId: groupId,
        });
        dispatch(addPrompt_deprecated({ groupId, prompt: newPrompt }));
      } catch (error) {
        handleError(error, 'Failed to create prompt');
      }
    },
    [dispatch, handleError, create_prompt],
  );

  // New: updates prompt in flat list
  const updatePrompt = useCallback(
    async (promptId: string, updates: Partial<Prompt>) => {
      const optimisticAction = updatePromptAction({ promptId, update: updates });
      try {
        dispatch(optimisticAction);
        const updatedPrompt = await update_prompt(promptId, updates);
        dispatch(updatePromptAction({ promptId, update: updatedPrompt }));
      } catch (error) {
        handleError(error, 'Failed to update prompt', optimisticAction);
      }
    },
    [dispatch, handleError, update_prompt],
  );

  // Deprecated: updates prompt in nested structure
  const updatePrompt_deprecated = useCallback(
    async (groupId: string, promptId: string, updates: Partial<Prompt>) => {
      const optimisticAction = updatePromptAction_deprecated({
        groupId,
        promptId,
        update: updates,
      });
      try {
        dispatch(optimisticAction);
        await update_prompt(promptId, updates);
      } catch (error) {
        handleError(error, 'Failed to update prompt', optimisticAction);
      }
    },
    [dispatch, handleError, update_prompt],
  );

  // New: removes prompt from flat list
  const removePrompt = useCallback(
    async (promptId: string) => {
      const optimisticAction = deletePrompt({ promptId });
      try {
        dispatch(optimisticAction);
        await delete_prompt(promptId);
      } catch (error) {
        handleError(error, 'Failed to delete prompt', optimisticAction);
      }
    },
    [dispatch, handleError, delete_prompt],
  );

  // Deprecated: removes prompt from nested structure
  const removePrompt_deprecated = useCallback(
    async (groupId: string, promptId: string) => {
      const optimisticAction = deletePrompt_deprecated({ groupId, promptId });
      try {
        dispatch(optimisticAction);
        await delete_prompt(promptId);
      } catch (error) {
        handleError(error, 'Failed to delete prompt', optimisticAction);
      }
    },
    [dispatch, handleError, delete_prompt],
  );

  const clonePrompt = useCallback(
    async (duplicatedPrompt: Prompt) => {
      try {
        const clonedPrompt = await create_prompt({
          name: `Copy of ${duplicatedPrompt.name} `,
          promptText: duplicatedPrompt.promptText,
          promptGroupId: duplicatedPrompt.promptGroupId ?? undefined,
        });
        dispatch(addPrompt({ prompt: clonedPrompt }));
      } catch (error) {
        handleError(error, 'Failed to duplicate prompt');
      }
    },
    [dispatch, handleError, create_prompt],
  );

  // Deprecated: clones prompt into a specific group
  const clonePrompt_deprecated = useCallback(
    async (groupId: string, duplicatedPrompt: Prompt) => {
      try {
        const clonedPrompt = await create_prompt({
          name: `Copy of ${duplicatedPrompt.name} `,
          promptText: duplicatedPrompt.promptText,
          promptGroupId: groupId,
        });
        dispatch(addPrompt_deprecated({ groupId, prompt: clonedPrompt }));
      } catch (error) {
        handleError(error, 'Failed to duplicate prompt');
      }
    },
    [dispatch, handleError, create_prompt],
  );

  const optimizePrompt = useCallback(
    async (promptText: string) => {
      try {
        const optimizedPrompt = await optimize_prompt(promptText);
        return optimizedPrompt;
      } catch (error) {
        handleError(error, 'Failed to optimize prompt');
      }
    },
    [handleError, optimize_prompt],
  );

  const selectPrompt = useCallback(
    (prompt: Prompt) => {
      dispatch(setSelectedPrompt({ prompt }));
    },
    [dispatch],
  );

  const unselectPrompt = useCallback(() => {
    dispatch(setSelectedPrompt({ prompt: null }));
  }, [dispatch]);

  const addEmptyPrompt = useCallback(
    (groupId: string) => {
      dispatch(addEmptyPromptAction({ groupId }));
    },
    [dispatch],
  );

  const removeEmptyPrompt = useCallback(
    (groupId: string) => {
      dispatch(removeEmptyPromptAction({ groupId }));
    },
    [dispatch],
  );

  return {
    loadPrompts,
    addEmptyPrompt,
    createPrompt,
    createPrompt_deprecated,
    updatePrompt,
    updatePrompt_deprecated,
    removePrompt,
    removePrompt_deprecated,
    clonePrompt,
    clonePrompt_deprecated,
    optimizePrompt,
    removeEmptyPrompt,

    selectPrompt,
    unselectPrompt,
  };
};
